/**
 * errorStateProps — map whatever `apiFetch` threw to what ErrorState shows.
 *
 * The one rule: **no API text reaches the page.** `detail` can be a Pydantic
 * dump ("Input should be a valid UUID … found `m` at 1"), a role name
 * ("Super Admin role required") or an env-var name ("set SLACK_CLIENT_ID").
 * Every sentence below is ours, chosen by status and error code. A feature
 * key is carried to the upgrade link (so /billing can say which feature),
 * never printed.
 *
 * Pure (no React), so it is unit-tested in errorStateProps.test.ts.
 */
import {
  ApiError,
  AuthError,
  ForbiddenError,
  GoneError,
  NetworkError,
  NotFoundError,
  PlanGateError,
  RateLimitError,
} from '@/lib/api/errors';

export type ErrorStateVariant = 'generic' | 'plan' | 'forbidden' | 'notFound';

export interface ErrorStateContent {
  variant: ErrorStateVariant;
  title: string;
  message: string;
  /** Where the plan variant's button goes (relative desk route or absolute upgrade URL). */
  upgradeHref?: string;
  upgradeLabel?: string;
  /** Whether a Retry button makes sense (transient failures only). */
  retryable: boolean;
}

/** The desk's plans page. `require_team_plan` and the feature gates point here. */
export const PLANS_HREF = '/billing/plans';

const FEATURE_KEY = /^[a-z][a-z0-9_]{0,63}$/;

function plansHref(reason: string | null, feature: string | null): string {
  const params = new URLSearchParams();
  if (reason && FEATURE_KEY.test(reason)) params.set('reason', reason);
  if (feature && FEATURE_KEY.test(feature)) params.set('feature', feature);
  const qs = params.toString();
  return qs ? `${PLANS_HREF}?${qs}` : PLANS_HREF;
}

/** Only an https URL the API built for this edition, else the desk route. */
function safeUpgradeUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

export interface ErrorStateOptions {
  /** What failed to load, lower case: "matters", "this matter". Used in sentences. */
  resource?: string;
}

export function errorStateProps(
  error: unknown,
  options: ErrorStateOptions = {},
): ErrorStateContent {
  const what = options.resource?.trim();

  if (error instanceof PlanGateError) {
    const code = error.errorCode;
    const feature = error.requiredFeature;
    if (code === 'team_plan_required') {
      return {
        variant: 'plan',
        title: 'Needs a multi-seat plan',
        message:
          'This works across a team, and your plan has one seat. Add seats or move to a multi-seat plan to use it.',
        upgradeHref: plansHref(code, null),
        upgradeLabel: 'See plans',
        retryable: false,
      };
    }
    if (code === 'trial_expired') {
      return {
        variant: 'plan',
        title: 'Your trial has ended',
        message: 'Choose a plan to keep working. Your documents are safe.',
        upgradeHref: plansHref(code, feature),
        upgradeLabel: 'Choose a plan',
        retryable: false,
      };
    }
    if (code === 'no_plan') {
      return {
        variant: 'plan',
        title: 'Choose a plan',
        message: 'Your account has no active plan. Choose one to continue.',
        upgradeHref: plansHref(code, feature),
        upgradeLabel: 'Choose a plan',
        retryable: false,
      };
    }
    if (code === 'payment_method_required') {
      return {
        variant: 'plan',
        title: 'Add a payment method',
        message: 'Your trial starts once a card is on file.',
        upgradeHref: '/subscribe',
        upgradeLabel: 'Add a card',
        retryable: false,
      };
    }
    if (error.limit !== null && error.used !== null && error.limit > 0) {
      return {
        variant: 'plan',
        title: 'You’ve used this month’s allowance',
        message: 'It resets with your billing cycle, or upgrade to get more now.',
        upgradeHref: safeUpgradeUrl(error.upgradeUrl) ?? plansHref(code, feature),
        upgradeLabel: 'Upgrade',
        retryable: false,
      };
    }
    return {
      variant: 'plan',
      title: 'Not included in your plan',
      message: what
        ? `Your current plan doesn’t include ${what}. Upgrade to use it.`
        : 'Your current plan doesn’t include this. Upgrade to use it.',
      upgradeHref: safeUpgradeUrl(error.upgradeUrl) ?? plansHref(code, feature),
      upgradeLabel: 'Upgrade',
      retryable: false,
    };
  }

  if (error instanceof ForbiddenError) {
    if (error.errorCode === 'feature_not_assigned') {
      return {
        variant: 'forbidden',
        title: 'Not turned on for you',
        message:
          'Your firm has this feature, but it isn’t enabled on your account. Ask your workspace admin.',
        retryable: false,
      };
    }
    return {
      variant: 'forbidden',
      title: 'You don’t have access',
      message: what
        ? `Your role can’t open ${what}. Ask your workspace admin if you need it.`
        : 'Your role can’t open this. Ask your workspace admin if you need it.',
      retryable: false,
    };
  }

  if (error instanceof NotFoundError) {
    return {
      variant: 'notFound',
      title: 'We can’t find that',
      message: what
        ? `We couldn’t find ${what}. It may have been deleted, or the link is wrong.`
        : 'It may have been deleted, or the link is wrong.',
      retryable: false,
    };
  }

  if (error instanceof GoneError) {
    return {
      variant: 'notFound',
      title: 'This link has expired',
      message: 'Ask for a new link.',
      retryable: false,
    };
  }

  if (error instanceof AuthError) {
    return {
      variant: 'generic',
      title: 'Your session has ended',
      message: 'Sign in again to continue.',
      retryable: false,
    };
  }

  if (error instanceof NetworkError) {
    return {
      variant: 'generic',
      title: 'Can’t reach the server',
      message: 'Check your connection, then try again.',
      retryable: true,
    };
  }

  if (error instanceof RateLimitError) {
    return {
      variant: 'generic',
      title: 'Too many requests',
      message: 'Wait a few seconds, then try again.',
      retryable: true,
    };
  }

  const transient = !(error instanceof ApiError) || error.status >= 500 || error.status === 0;
  return {
    variant: 'generic',
    title: what ? `Couldn’t load ${what}` : 'Couldn’t load this',
    message: transient
      ? 'Something went wrong on our side. Try again in a moment.'
      : 'This request couldn’t be completed. Try again, or contact support if it keeps happening.',
    retryable: true,
  };
}
